import type { Map as MapboxMap } from "mapbox-gl";
import type { PostersGeoJson } from "../../api/posters";

const SOURCE_ID = "posters";
const LAYER_CIRCLE = "posters-circle";
const LAYER_SELECTED = "posters-circle-selected";
const LAYER_LABEL = "posters-label";

export const POSTERS_LAYER_IDS = [LAYER_LABEL, LAYER_SELECTED, LAYER_CIRCLE];
export const POSTERS_CLICKABLE_LAYER_ID = LAYER_CIRCLE;

export const ensurePostersLayer = (map: MapboxMap): void => {
    if (!map.isStyleLoaded()) return;

    if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] }
        });
    }

    if (!map.getLayer(LAYER_CIRCLE)) {
        map.addLayer({
            id: LAYER_CIRCLE,
            type: "circle",
            source: SOURCE_ID,
            paint: {
                "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 8, 14, 12, 18, 18],
                "circle-color": [
                    "match",
                    ["get", "status"],
                    "Active", "#E87716",
                    "TakenDown", "#64748b",
                    "Vandalized", "#D92626",
                    "#E87716"
                ],
                "circle-stroke-color": "#ffffff",
                "circle-stroke-width": 2,
                "circle-opacity": [
                    "case",
                    ["==", ["get", "status"], "Active"], 1.0,
                    0.55
                ]
            }
        });
    }

    if (!map.getLayer(LAYER_SELECTED)) {
        map.addLayer({
            id: LAYER_SELECTED,
            type: "circle",
            source: SOURCE_ID,
            filter: ["==", ["id"], ""],
            paint: {
                "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 14, 14, 19, 18, 26],
                "circle-color": "rgba(0,0,0,0)",
                "circle-stroke-color": "#E87716",
                "circle-stroke-width": 3
            }
        });
    }

    if (!map.getLayer(LAYER_LABEL)) {
        map.addLayer({
            id: LAYER_LABEL,
            type: "symbol",
            source: SOURCE_ID,
            filter: [">=", ["get", "quantity"], 2],
            layout: {
                "text-field": ["to-string", ["get", "quantity"]],
                "text-size": 11,
                "text-font": ["DIN Offc Pro Bold", "Arial Unicode MS Bold"],
                "text-anchor": "center"
            },
            paint: {
                "text-color": "#ffffff",
                "text-halo-color": "rgba(0,0,0,0.3)",
                "text-halo-width": 0.5
            }
        });
    }
};

/**
 * Nedtagna affischer är historik. De döljs som standard så att kartan visar det som
 * faktiskt sitter uppe – samma regel som statistikens "Uppe nu". Statusen kommer som
 * sträng från API:et, se `PosterStatus`.
 */
export const setPostersShowTakenDown = (map: MapboxMap, showTakenDown: boolean): void => {
    const notTakenDown: unknown[] = ["!=", ["get", "status"], "TakenDown"];

    if (map.getLayer(LAYER_CIRCLE)) {
        map.setFilter(LAYER_CIRCLE, showTakenDown ? null : (notTakenDown as never));
    }
    if (map.getLayer(LAYER_LABEL)) {
        map.setFilter(
            LAYER_LABEL,
            (showTakenDown
                ? [">=", ["get", "quantity"], 2]
                : ["all", [">=", ["get", "quantity"], 2], notTakenDown]) as never
        );
    }
};

export const updatePostersLayer = (map: MapboxMap, data: PostersGeoJson | null): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") return;
    (source as mapboxgl.GeoJSONSource).setData(
        data ?? ({ type: "FeatureCollection", features: [] } as any)
    );
};

export const setPostersSelectedId = (map: MapboxMap, posterId: string | null): void => {
    if (!map.getLayer(LAYER_SELECTED)) return;
    map.setFilter(LAYER_SELECTED, posterId ? ["==", ["id"], posterId] : ["==", ["id"], ""]);
};

export const removePostersLayer = (map: MapboxMap): void => {
    for (const layerId of POSTERS_LAYER_IDS) {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
    }
    if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
};
